import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { apiRootUrl, fileUrl } from 'common/utils/network/endpoints';
import { MemberPersonalDocument, MemberPersonalDocumentData } from 'types/Document/MemberDocument';

const transformGetToDos = (todos: any[]) => (todos || [])
  .map((todo: any) => ({
    ...todo,
    status: todo.actions
      .some(({ is_assigned: isAssigned, status }: any) => (isAssigned || status)),
    actions: todo.actions
      .map((action: any) => ({
        ...action,
        status: action?.is_assigned || action?.status || false,
        html_content: action?.to_do_title || action?.html_content || '',
      })),
  }));

export const memberApi = createApi({
  reducerPath: 'memberApi',
  baseQuery: fetchBaseQuery({
    baseUrl: apiRootUrl,
    prepareHeaders: (headers, { getState }) => {
      const { token } = (getState() as any).user
      headers.set('authorization', `Bearer ${token}`)
      return headers
    },
  }),
  tagTypes: [
    'Reminder',
    'Section',
    'AdviceColumn',
    'Learning',
    'LearningData',
    'Booking',
    'ToDoTask',
    'FcBooking',
    'FCWelcomePage',
    'Survey',
    'Dashboard',
    'Triggers',
    'HistoryLog',
    'Documents',
    'DocumentsForSigning',
    'DocusignSigningUrl',
    'MyDocuments',
  ],
  endpoints: (builder) => ({
    signIn: builder.mutation({
      query: (data) => ({
        url: 'member/login',
        method: 'POST',
        body: data,
      }),
    }),
    logOut: builder.mutation({
      query: () => ({
        url: 'member/logout',
        method: 'POST',
      }),
    }),
    getPublicSetting: builder.query<any, any>({
      query: (clientId: string) => ({
        url: `clients/${clientId}/public-settings`,
        method: 'GET',
      }),
      transformResponse: (response: any) => response.data,
    }),
    register: builder.mutation({
      query: (data) => ({
        url: 'member/verify',
        method: 'PUT',
        body: data,
      }),
    }),
    verification: builder.mutation({
      query: (data) => ({
        url: 'member/verification',
        method: 'POST',
        body: data,
      }),
    }),
    registration: builder.mutation({
      query: (data) => ({
        url: 'member/registration',
        method: 'POST',
        body: data,
      }),
    }),
    confirmPassword: builder.mutation({
      query: (data) => ({
        url: 'member/register',
        method: 'PUT',
        body: data,
      }),
    }),
    getAdviceColumns: builder.query<any, void>({
      query: () => 'member/advice/page',
      transformResponse: (response: any) => response.data,
      providesTags: ['AdviceColumn'],
    }),
    getAdviceColumnStatus: builder.query({
      query: (elementCode) => `member/advice/advice/${elementCode}`,
      transformResponse: (response: any, meta, arg) => response.data,
      providesTags: ['AdviceColumn'],
    }),
    saveAdviceColumnStatus: builder.mutation({
      query: ({ elementCode, data }) => ({
        url: `member/advice/advice/${elementCode}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['AdviceColumn'],
    }),
    getSectionFields: builder.query({
      query: (name) => `member/profile/hydrate/${name}`,
      transformResponse: (response: any, meta, arg) => response.data,
    }),
    getSavedFields: builder.query({
      query: (name) => `member/profile/get/${name}`,
      transformResponse: (response: any, meta, arg) =>
        JSON.parse(response.data.data),
      providesTags: ['Section'],
    }),
    getSectionsStatus: builder.query({
      query: (name) => 'member/profile/status',
      transformResponse: (response: any, meta, arg) => response.data,
      providesTags: ['Section'],
    }),
    postSectionFields: builder.mutation({
      query: ({ name, data }) => ({
        url: `member/profile/save/${name}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Section', 'AdviceColumn'],
    }),
    postMemberEmail: builder.mutation<any, { email: string }>({
      query: (formData) => ({
        url: 'member/forgotpassword',
        method: 'POST',
        body: formData,
      }),
    }),
    memberResetPassword: builder.mutation<
      any,
      {
        email: string
        token: string | null
        password: string
        cpassword: string
      }
    >({
      query: (formData) => ({
        url: 'member/resetpassword',
        method: 'POST',
        body: formData,
      }),
    }),
    postBooking: builder.mutation<any, any>({
      query: (formData) => ({
        url: 'member/create/booking',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['FcBooking', 'Booking', 'AdviceColumn'],
    }),
    getReminder: builder.query<any, void>({
      query: () => ({
        url: 'member/reminder',
      }),
      transformResponse: (response: any, meta, arg) => response.data,
      providesTags: ['Reminder'],
    }),
    saveReminder: builder.mutation<any, any>({
      query: (formData) => ({
        url: 'member/reminder',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Reminder'],
    }),
    getBooking: builder.query<any, void>({
      query: () => 'member/advice/page',
      transformResponse: (response: any) =>
        response.data?.advice_features,
      providesTags: ['Booking'],
    }),
    postBookingState: builder.mutation<any, any>({
      query: (bookingId) => ({
        url: 'member/advice/advice/booking',
        method: 'POST',
        body: {
          booked: true,
          booking_id: bookingId,
        },
      }),
      invalidatesTags: ['Booking'],
    }),
    getLearningPageData: builder.query<any, string>({
      query: (clientName: string) => `${clientName ? `${clientName}/` : ''}member/learning/page`,
      transformResponse: (response: any, meta, arg) => response.data,
      providesTags: ['LearningData'],
    }),
    learningModuleVideoWatched: builder.mutation({
      query: ({ zoneModuleId, status }) => ({
        url: `member/learning/modules/${zoneModuleId}/video-watched`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `status=${status}`,
      }),
      transformResponse: (response: any, meta, arg) => response.data,
      invalidatesTags: ['LearningData'],
    }),
    getAzureStorageUrl: builder.query<any | null, any>({
      query: ({
        fileName,
        filePath,
      }: {
        fileName: string
        filePath: string
      }) => ({
        url: fileUrl,
        method: 'GET',
        params: {
          file: fileName,
          folder: `storage/app/${filePath}`,
        },
      }),
      transformResponse: (response: any) => response.data,
    }),

    getRetirementQuotation: builder.query({
      query: (elementCode) => 'content/quotation_page',
      transformResponse: (response: any, meta, arg) => response.data,
    }),
    getBasicAdvice: builder.query({
      query: (elementCode) => 'product/content/basic_advice/basic_advice_page',
      transformResponse: (response: any, meta, arg) => response.data,
    }),
    cancelBooking: builder.mutation<any, any>({
      query: (id) => ({
        url: 'member/cancel/booking',
        method: 'POST',
        body: { member_booking_id: id },
      }),
      invalidatesTags: ['AdviceColumn', 'Booking', 'FcBooking'],
    }),
    getSimplyBookServices: builder.query({
      query: (elementCode) => 'admin/booking/services',
      transformResponse: (response: any, meta, arg) => response.data,
    }),
    getFinancialCoachingQuestions: builder.query({
      query: (elementCode) => 'financial-coaching/health-check/questions',
      transformResponse: (response: any, meta, arg) => response.data,
    }),
    getFinancialCoachingQuestionsSummary: builder.query({
      query: (elementCode) => 'financial-coaching/health-check/thank-you-page',
      transformResponse: (response: any, meta, arg) => response.data,
    }),
    saveFinancialCoachingAnswer: builder.mutation({
      query: ({ code, optionValue, age }) => ({
        url: `financial-coaching/health-check/questions/${code}/response`,
        method: 'PUT',
        body: { option: optionValue, age },
      }),
      invalidatesTags: ['FCWelcomePage'],
    }),
    getBookingData: builder.query<any, any>({
      query: (bookingId) => ({
        url: `member/get/booking/${bookingId}`,
        method: 'GET',
      }),
      providesTags: ['Booking'],
      transformResponse: (response: any) => response.data,
    }),
    getHomePage: builder.query<any, void>({
      query: (elementCode) => 'content/home_page',
      transformResponse: (response: any, meta, arg) => response.data,
    }),
    getLearningPage: builder.query<any, void>({
      query: () =>
        'product/content/learning_zone/learning_zone_page',
      providesTags: ['Learning'],
      transformResponse: (response: any, meta, arg) => response.data,
    }),
    getPremierAdvicePage: builder.query<any, void>({
      query: () =>
        'product/content/premier_advice/premier_advice_page',
      transformResponse: (response: any, meta, arg) => response.data,
    }),
    getWatchVideo: builder.query({
      query: (elementCode) =>
        'product/content/premier_advice/introductory_video',
      transformResponse: (response: any, meta, arg) => response.data,
    }),
    getToDoList: builder.query<any, void>({
      query: () => 'financial-coaching/todo-actions',
      transformResponse: (response: any, meta, arg) => transformGetToDos(response.data),
      providesTags: ['ToDoTask'],
    }),
    saveToDoTaskStatus: builder.mutation({
      query: ({ code, status }) => ({
        url: `financial-coaching/todo-actions/${code}/response`,
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: ['ToDoTask'],
    }),
    getColors: builder.query({
      query: (elementCode) => 'theme-settings/colors',
      transformResponse: (response: any, meta, arg) => response.data,
    }),
    saveFCQuestionnaireComment: builder.mutation<any, any>({
      query: ({ comment, healthCheckId }) => ({
        url: `financial-coaching/health-check/${healthCheckId}/comment`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `comment=${comment}`,
      }),
    }),
    getFCWelcomePage: builder.query<any, void>({
      query: () => 'financial-coaching/health-check/welcome-page',
      transformResponse: (response: any, meta, arg) => response.data,
      providesTags: ['FCWelcomePage'],
    }),
    getPublicSurveyResult: builder.query<any, any>({
      query: ({ clientId, surveyId }) => `clients/${clientId}/surveys/${surveyId}`,
      transformResponse: (response: any, meta, arg) => response.data,
    }),
    getFCReportData: builder.query({
      query: ({ scoreId }) =>
        `financial-coaching/health-check/${scoreId}/pdf-data`,
      transformResponse: (response: any, meta, arg) => response.data,
    }),
    getInTakeQuestions: builder.query({
      query: () => 'financial-coaching/intake-questions',
      transformResponse: (response: any, meta, arg) => response.data,
    }),
    saveInTakeQuestions: builder.mutation({
      query: ({ questions, bookingId }) => ({
        url: 'financial-coaching/intake-questions',
        method: 'POST',
        body: {
          id: bookingId,
          questions,
        },
      }),
    }),
    getFCBookingConsent: builder.query({
      query: () => 'financial-coaching/booking-consent',
      transformResponse: (response: any, meta, arg) => response.data,
    }),
    getFinancialCoachingColumns: builder.query<any, void>({
      query: () => 'member/financial-coaching',
      transformResponse: (response: any, meta, arg) => response.data,
      providesTags: ['FcBooking'],
    }),
    getBookingSubaccount: builder.query({
      query: () => 'member/subbaccount/booking',
      transformResponse: (response: any, meta, arg) => response.data,
    }),
    getFCBookingConfig: builder.query({
      query: () => 'member/financial-coaching/onsched-booking-config',
      transformResponse: (response: any, meta, arg) => response.data,
    }),
    getSurveyList: builder.query<any, any>({
      query: (clientId) => ({
        url: 'member/surveys/survey-form',
        method: 'GET',
      }),
      transformResponse: (response: any) => response.data,
      providesTags: ['Survey'],
    }),
    completeSurvey: builder.mutation({
      query: ({ payload, surveyId }) => ({
        url: `member/surveys/${surveyId}/result`,
        method: 'POST',
        body: payload,
      }),
    }),
    completeQuestion: builder.mutation({
      query: ({ payload, surveyId }) => ({
        url: `member/surveys/${surveyId}/questionnaire-result`,
        method: 'POST',
        body: payload,
      }),
    }),
    getDashboardData: builder.query<any, void>({
      query: () => 'member/dashboard',
      transformResponse: (response: any) => response.data,
      providesTags: ['Dashboard'],
    }),
    getMemberCustomizationData: builder.query<any, void>({
      query: () => 'member/processflow',
      transformResponse: (response: any) => response.data,
    }),
    getMemberTriggersData: builder.query<any, void>({
      query: () => 'member/triggers-data',
      transformResponse: (response: any) => response.data,
      providesTags: ['Triggers'],
    }),
    saveMemberTriggersData: builder.mutation<any, any>({
      query: (data) => ({
        url: 'member/triggers-data',
        method: 'POST',
        body: { data: JSON.stringify({ ...(data || {}) }) },
      }),
      invalidatesTags: ['Triggers'],
    }),
    getMemberHistoryLog: builder.query<any, void>({
      query: () => 'member/history-log',
      transformResponse: (response: any) => response.data,
      providesTags: ['HistoryLog'],
    }),
    saveMemberHistoryLog: builder.mutation<any, any>({
      query: ({ data, reference }) => ({
        url: 'member/history-log',
        method: 'POST',
        body: {
          reference,
          data: JSON.stringify({ ...(data || {}) }),
        },
      }),
      invalidatesTags: ['HistoryLog'],
    }),
    addEmailToQueue: builder.mutation<any, any>({
      query: ({ email }) => ({
        url: 'member/email/send',
        method: 'POST',
        body: {
          ...email,
        },
      }),
    }),
    getDocumentAsBase64: builder.query<any, { reference: string }>({
      query: ({ reference }) => `member/documents/base64/${reference}`,
      transformResponse: (response: any) => response.data,
      providesTags: ['Documents'],
    }),
    // saveDocumentAgreement: builder.mutation<any, any>({
    //   query: ({ data }) => ({
    //     url: 'member/documents/tc/action',
    //     method: 'POST',
    //     body: data,
    //   }),
    // }),
    saveDocumentAgreement: builder.mutation<any, any>({
      query: ({ payload, documentReference }) => ({
        url: `member/documents/${documentReference}/action`,
        method: 'POST',
        body: payload,
      }),
    }),
    getMemberDocumentAsBase64: builder.query<any, {
      documentReference: string;
      documentVersionId?: number
    }>({
      query: ({ documentReference, documentVersionId = null }) =>
        `member/my-documents/base64?name=${documentReference}${documentVersionId ? `&id=${documentVersionId}` : ''}`,
      transformResponse: (response: any) => response.data,
      providesTags: ['MyDocuments'],
    }),
    saveMemberDocumentFeedback: builder.mutation<any, any>({
      query: ({ payload, documentId }) => ({
        url: `member/my-documents/${documentId}/action`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['MyDocuments'],
    }),
    docusignSigningUrl: builder.mutation<{envelopeId: string, url: string}, {
      return_url: string,
      document_id: string,
      documentReference: string
    }>({
      query: ({
        return_url, document_id, documentReference,
      }) => ({
        url: `member/documents/${documentReference}/sign/envelope`,
        method: 'POST',
        body: {
          return_url,
          document_id,
        },
      }),
    }),
    getDocumentsForSigning: builder.query<any, {documentReference: string }>({
      query: ({ documentReference }) => ({
        url: `/member/documents/${documentReference}/details`,
        method: 'GET',
      }),
      providesTags: ['DocumentsForSigning'],
      transformResponse: (response: any) => response?.data,
    }),
    getMyDocuments: builder.query<MemberPersonalDocument[], void>({
      query: () => ({
        url: '/member/my-documents',
        method: 'GET',
      }),
      providesTags: ['DocumentsForSigning'],
      transformResponse: (response: any) => response?.data as MemberPersonalDocument[],
    }),
    getMyDocument: builder.query<any, any>({
      query: ({ documentReference }) => ({
        url: `/member/my-documents/${documentReference}/base64`,
        method: 'GET',
      }),
      transformResponse: (response: any) => response?.data,
    }),
    getMyDocumentHistory: builder.query<MemberPersonalDocumentData, any>({
      query: ({ id }) => ({
        url: `/member/my-documents/history?id=${id}`,
        method: 'GET',
      }),
      transformResponse: (response: any) => response?.data,
    }),
  }),
})

export const {
  useSignInMutation,
  useLogOutMutation,
  useRegisterMutation,
  useGetPublicSettingQuery,
  useRegistrationMutation,
  useVerificationMutation,
  useConfirmPasswordMutation,
  useGetAdviceColumnsQuery,
  useGetSectionFieldsQuery,
  useGetSavedFieldsQuery,
  useGetSectionsStatusQuery,
  useGetAdviceColumnStatusQuery,
  useSaveAdviceColumnStatusMutation,
  usePostSectionFieldsMutation,
  usePostMemberEmailMutation,
  useMemberResetPasswordMutation,
  usePostBookingMutation,
  useGetReminderQuery,
  useSaveReminderMutation,
  useGetBookingQuery,
  usePostBookingStateMutation,
  useGetLearningPageDataQuery,
  useLearningModuleVideoWatchedMutation,
  useGetAzureStorageUrlQuery,
  useCancelBookingMutation,
  useGetFinancialCoachingQuestionsQuery,
  useSaveFinancialCoachingAnswerMutation,
  useGetFinancialCoachingQuestionsSummaryQuery,
  useGetRetirementQuotationQuery,
  useGetBasicAdviceQuery,
  useGetHomePageQuery,
  useGetLearningPageQuery,
  useGetPremierAdvicePageQuery,
  useGetWatchVideoQuery,
  useGetToDoListQuery,
  useSaveToDoTaskStatusMutation,
  useGetColorsQuery,
  useSaveFCQuestionnaireCommentMutation,
  useGetFCWelcomePageQuery,
  useGetPublicSurveyResultQuery,
  useGetFCReportDataQuery,
  useGetInTakeQuestionsQuery,
  useSaveInTakeQuestionsMutation,
  useGetFCBookingConsentQuery,
  useGetBookingSubaccountQuery,
  useGetFinancialCoachingColumnsQuery,
  useGetFCBookingConfigQuery,
  useLazyGetSurveyListQuery,
  useCompleteSurveyMutation,
  useCompleteQuestionMutation,
  useGetDashboardDataQuery,
  useGetSimplyBookServicesQuery,
  useGetMemberCustomizationDataQuery,
  useGetMemberTriggersDataQuery,
  useSaveMemberTriggersDataMutation,
  useGetMemberHistoryLogQuery,
  useSaveMemberHistoryLogMutation,
  useAddEmailToQueueMutation,
  useGetDocumentAsBase64Query,
  useLazyGetDocumentAsBase64Query,
  useSaveDocumentAgreementMutation,
  useGetDocumentsForSigningQuery,
  useDocusignSigningUrlMutation,
  useGetMyDocumentsQuery, // personal docs
  useGetMyDocumentQuery, // personal doc
  useLazyGetMyDocumentQuery, // personal doc lazy
  useLazyGetMemberDocumentAsBase64Query,
  useGetMemberDocumentAsBase64Query,
  useSaveMemberDocumentFeedbackMutation, // personal doc feedback
  useLazyGetMyDocumentHistoryQuery,
} = memberApi
